/**
 * Converts a plain-text description (with line breaks and bullet-list
 * conventions) into safe HTML for Bootstrap popovers.
 *
 * Supported conventions:
 *  - Lines starting with `- ` or `* ` become `<li>` items inside a `<ul>`.
 *  - Consecutive bullet lines are grouped into a single `<ul>`.
 *  - Regular line breaks (`\n`) become `<br>`.
 *  - HTML special characters are escaped to prevent XSS.
 */
export function formatDescription(raw: string | null | undefined): string {
  if (!raw) return '';

  // 1. Escape HTML entities
  const escaped = raw
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

  // 2. Split into lines
  const lines = escaped.split('\n');

  // 3. Group consecutive bullet lines into <ul> blocks
  const fragments: string[] = [];
  let bulletBuffer: string[] = [];

  const flushBullets = () => {
    if (bulletBuffer.length === 0) return;
    fragments.push(
      '<ul class="mb-1 ps-3">' +
        bulletBuffer.map((b) => `<li>${b}</li>`).join('') +
        '</ul>',
    );
    bulletBuffer = [];
  };

  for (const line of lines) {
    const bulletMatch = line.match(/^\s*[-*]\s+(.*)/);
    if (bulletMatch) {
      bulletBuffer.push(bulletMatch[1]);
    } else {
      flushBullets();
      fragments.push(line);
    }
  }
  flushBullets();

  // 4. Join non-bullet fragments with <br>, but skip <br> right before/after a <ul>
  let html = '';
  for (let i = 0; i < fragments.length; i++) {
    const frag = fragments[i];
    if (i > 0) {
      const prev = fragments[i - 1];
      const prevIsUl = prev.startsWith('<ul');
      const currIsUl = frag.startsWith('<ul');
      if (!prevIsUl && !currIsUl) {
        html += '<br>';
      }
    }
    html += frag;
  }

  return html;
}
